/**
 * QuickDelivery Pro — licence key + activation token primitives.
 *
 * Two artefacts are involved:
 *
 *   1. Licence key  — `QDP-XXXXX-XXXXX-XXXXX`, 75 bits of entropy in Crockford
 *      base32. Short enough to read over the phone or type on a bus. Issued by
 *      the Stripe webhook, emailed to the buyer. Only its SHA-256 hash is
 *      stored (in Stripe customer metadata), so a leak of that store cannot be
 *      replayed as a key.
 *
 *   2. Activation token — a compact Ed25519-signed payload the server returns
 *      when a key is redeemed for a specific device. The app verifies it
 *      offline on every launch against the public key baked into the bundle,
 *      so a working licence never needs the network again until `exp`.
 *
 * v1 used a 32-bit non-cryptographic hash of `deviceId + hardcoded salt` — the
 * salt shipped in the client bundle, so anyone could mint keys and the whole
 * key space was 8 hex characters. Everything here replaces that.
 */

import * as ed from '@noble/ed25519';
import { sha512 } from '@noble/hashes/sha512';
import { sha256 } from '@noble/hashes/sha256';
import { LICENSE_PUBLIC_KEY, OFFLINE_GRACE_DAYS, type PlanId } from './config';

// @noble/ed25519 v2 ships async-only by default; wiring the sync SHA-512 in
// lets us verify without an await deep inside a React render path.
ed.etc.sha512Sync = (...m: Uint8Array[]) => sha512(ed.etc.concatBytes(...m));

/** Crockford base32 — no I, L, O or U, so 1/l and 0/O cannot be confused. */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const KEY_PREFIX = 'QDP';
const KEY_GROUPS = 3;
const KEY_GROUP_LEN = 5;

export const DAY_MS = 86_400_000;

// ---------------------------------------------------------------------------
// base64url
// ---------------------------------------------------------------------------

export function toB64Url(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  const b64 = typeof btoa === 'function' ? btoa(bin) : Buffer.from(bytes).toString('base64');
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromB64Url(s: string): Uint8Array {
  const pad = (4 - (s.length % 4)) % 4;
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat(pad);
  if (typeof atob === 'function') {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }
  return new Uint8Array(Buffer.from(b64, 'base64'));
}

const utf8 = (s: string) => new TextEncoder().encode(s);

// ---------------------------------------------------------------------------
// Licence keys
// ---------------------------------------------------------------------------

/**
 * Mint a licence key from cryptographically secure randomness.
 * Server-side only — the app never generates keys.
 */
export function generateLicenseKey(randomBytes: Uint8Array): string {
  const needed = KEY_GROUPS * KEY_GROUP_LEN;
  if (randomBytes.length < needed) {
    throw new Error(`generateLicenseKey needs at least ${needed} random bytes`);
  }
  const chars: string[] = [];
  for (let i = 0; i < needed; i++) chars.push(ALPHABET[randomBytes[i] % ALPHABET.length]);
  const groups: string[] = [];
  for (let g = 0; g < KEY_GROUPS; g++) {
    groups.push(chars.slice(g * KEY_GROUP_LEN, (g + 1) * KEY_GROUP_LEN).join(''));
  }
  return `${KEY_PREFIX}-${groups.join('-')}`;
}

/**
 * Fold user typing variance away: strip separators, upper-case, and map the
 * characters Crockford treats as aliases (O→0, I/L→1) before validating.
 */
export function normalizeLicenseKey(input: string): string {
  const body = input
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/^QDP/, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1')
    .replace(/U/g, 'V');
  const groups: string[] = [];
  for (let g = 0; g < KEY_GROUPS; g++) {
    groups.push(body.slice(g * KEY_GROUP_LEN, (g + 1) * KEY_GROUP_LEN));
  }
  return `${KEY_PREFIX}-${groups.join('-')}`;
}

export function isWellFormedLicenseKey(input: string): boolean {
  const body = normalizeLicenseKey(input).slice(KEY_PREFIX.length + 1).replace(/-/g, '');
  return body.length === KEY_GROUPS * KEY_GROUP_LEN && [...body].every((c) => ALPHABET.includes(c));
}

/** Stable lookup handle for a key. Only this ever reaches storage. */
export function hashLicenseKey(key: string): string {
  return toB64Url(sha256(utf8(normalizeLicenseKey(key))));
}

// ---------------------------------------------------------------------------
// Activation tokens
// ---------------------------------------------------------------------------

export interface ActivationClaims {
  /** Stripe customer id the licence belongs to. */
  sub: string;
  plan: Exclude<PlanId, 'free'>;
  /** Device fingerprint this token is bound to. */
  did: string;
  /** Issued at (ms since epoch). */
  iat: number;
  /** Hard expiry (ms since epoch): billing period end + offline grace. */
  exp: number;
  /** Masked key shown in the UI, e.g. `QDP-…-WX3QZ`. */
  ref: string;
}

/** `<base64url(json)>.<base64url(sig)>` */
export type ActivationToken = string;

/** Server-side. `privateKey` is the raw 32-byte Ed25519 seed. */
export function signActivationToken(claims: ActivationClaims, privateKey: Uint8Array): ActivationToken {
  const body = toB64Url(utf8(JSON.stringify(claims)));
  const sig = ed.sign(utf8(body), privateKey);
  return `${body}.${toB64Url(sig)}`;
}

export type VerifyResult =
  | { valid: true; claims: ActivationClaims }
  | { valid: false; reason: 'malformed' | 'bad-signature' | 'expired' | 'wrong-device' };

/**
 * Verify a token offline. `deviceId` is checked so that copying a token file
 * between phones does not multiply a single-device licence.
 */
export function verifyActivationToken(
  token: ActivationToken,
  deviceId: string,
  now: number = Date.now(),
  publicKey: string = LICENSE_PUBLIC_KEY,
): VerifyResult {
  const dot = token.indexOf('.');
  if (dot <= 0 || dot === token.length - 1) return { valid: false, reason: 'malformed' };

  const body = token.slice(0, dot);
  let claims: ActivationClaims;
  try {
    if (!ed.verify(fromB64Url(token.slice(dot + 1)), utf8(body), fromB64Url(publicKey))) {
      return { valid: false, reason: 'bad-signature' };
    }
    claims = JSON.parse(new TextDecoder().decode(fromB64Url(body)));
  } catch {
    return { valid: false, reason: 'malformed' };
  }

  if (typeof claims.exp !== 'number' || typeof claims.did !== 'string') {
    return { valid: false, reason: 'malformed' };
  }
  if (claims.did !== deviceId) return { valid: false, reason: 'wrong-device' };
  if (now > claims.exp) return { valid: false, reason: 'expired' };
  return { valid: true, claims };
}

/** Expiry to stamp on a token for a subscription ending at `periodEndMs`. */
export function expiryForPeriod(periodEndMs: number): number {
  return periodEndMs + OFFLINE_GRACE_DAYS * DAY_MS;
}

/** Lifetime licences get a fixed far-future expiry rather than `Infinity`. */
export const LIFETIME_EXPIRY = Date.UTC(2099, 0, 1);

export function maskLicenseKey(key: string): string {
  const norm = normalizeLicenseKey(key);
  return `${KEY_PREFIX}-…-${norm.slice(-KEY_GROUP_LEN)}`;
}
